"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const DismissableTooltipPosition_1 = require("./DismissableTooltipPosition");
const DismissableToolTipListenerOption_1 = require("./DismissableToolTipListenerOption");
class DismissableTooltip {
    static attach(associatedElement, action, options) {
        associatedElement.style.position = "relative";
        const { tooltip, tooltipContent } = this.createTooltip();
        DismissableTooltip.addListeners(associatedElement, action, tooltip, tooltipContent, options);
    }
    static createTooltip() {
        const tooltip = document.createElement("div");
        // Should probably handle tabbing as well
        tooltip.addEventListener("click", event => {
            event.stopPropagation();
        });
        tooltip.classList.add("dismissable-tooltip");
        const tooltipContent = document.createElement("div");
        tooltipContent.classList.add("tooltip-content");
        const toolTipArrow = document.createElement("div");
        toolTipArrow.classList.add("tooltip-arrow");
        tooltip.appendChild(tooltipContent);
        tooltip.appendChild(toolTipArrow);
        return { tooltip, tooltipContent };
    }
    static addListeners(associatedElement, action, tooltip, tooltipContent, options) {
        const position = options?.position ?? DismissableTooltipPosition_1.DismissableTooltipPosition.AUTO;
        const listeners = options?.listeners ?? [DismissableToolTipListenerOption_1.DismissableToolTipListenerOption.CLICK];
        const listenerSet = new Set(listeners);
        for (const listener of Array.from(listenerSet)) {
            switch (listener) {
                case DismissableToolTipListenerOption_1.DismissableToolTipListenerOption.CLICK:
                    associatedElement.addEventListener("click", event => {
                        DismissableTooltip.showOrHide(tooltip, tooltipContent, event, {
                            action,
                            associatedElement,
                            listeners,
                            position
                        });
                    });
                    break;
                case DismissableToolTipListenerOption_1.DismissableToolTipListenerOption.HOVER:
                    associatedElement.addEventListener("mouseenter", event => {
                        DismissableTooltip.showOrHide(tooltip, tooltipContent, event, {
                            action,
                            associatedElement,
                            listeners,
                            position
                        });
                    });
                    associatedElement.addEventListener("mouseleave", event => {
                        DismissableTooltip.showOrHide(tooltip, tooltipContent, event, {
                            action,
                            associatedElement,
                            listeners,
                            position
                        });
                    });
                    break;
                case DismissableToolTipListenerOption_1.DismissableToolTipListenerOption.FOCUS:
                    associatedElement.addEventListener("focus", event => {
                        DismissableTooltip.showOrHide(tooltip, tooltipContent, event, {
                            action,
                            associatedElement,
                            listeners,
                            position
                        });
                    });
                    associatedElement.addEventListener("focusout", event => {
                        DismissableTooltip.showOrHide(tooltip, tooltipContent, event, {
                            action,
                            associatedElement,
                            listeners,
                            position
                        });
                    });
                    break;
                case DismissableToolTipListenerOption_1.DismissableToolTipListenerOption.ENTER_AND_SPACE:
                    associatedElement.addEventListener("keydown", event => {
                        if (event.key === " ") {
                            event.preventDefault();
                        }
                    });
                    associatedElement.addEventListener("keyup", event => {
                        DismissableTooltip.showOrHide(tooltip, tooltipContent, event, {
                            action,
                            associatedElement,
                            listeners,
                            position
                        });
                    });
                    break;
            }
        }
    }
    static async showOrHide(tooltip, tooltipContent, event, { action, position, associatedElement }) {
        DismissableTooltip.resetTooltip(associatedElement, tooltip, tooltipContent);
        if (event.type === "focusout" || event.type === "mouseleave") {
            tooltip.classList.remove("visible");
            return;
        }
        if (!tooltip.classList.contains("visible")) {
            const result = await action(event);
            DismissableTooltip.render(associatedElement, tooltip, tooltipContent, result);
            DismissableTooltip.positionTooltip(associatedElement, tooltip, position);
        }
        tooltip.classList.toggle("visible", !tooltip.classList.contains("visible"));
    }
    static resetTooltip(associatedElement, tooltip, tooltipContent) {
        while (tooltipContent.firstChild) {
            tooltipContent.removeChild(tooltipContent.firstChild);
        }
        if (associatedElement.contains(tooltip)) {
            associatedElement.removeChild(tooltip);
        }
    }
    static render(associatedElement, tooltip, tooltipContent, result) {
        if (typeof result === "string") {
            tooltipContent.textContent = result;
        }
        else if (result instanceof HTMLElement) {
            tooltipContent.appendChild(result);
        }
        else {
            throw new Error(`Unknown type ${result}`);
        }
        associatedElement.appendChild(tooltip);
    }
    static positionTooltip(associatedElement, tooltip, position) {
        if (position === DismissableTooltipPosition_1.DismissableTooltipPosition.AUTO) {
            for (const positionElement of Object.values(DismissableTooltipPosition_1.DismissableTooltipPosition)) {
                if (positionElement === DismissableTooltipPosition_1.DismissableTooltipPosition.AUTO) {
                    continue;
                }
                const point = DismissableTooltip.getTooltipPoint(associatedElement, tooltip, positionElement);
                DismissableTooltip.transformXY(tooltip, positionElement, point.x, point.y);
                if (DismissableTooltip.isElementInDom(tooltip.getBoundingClientRect())) {
                    return;
                }
                tooltip.classList.remove(positionElement);
            }
            // default to top if it wasn't possible to find a render position
            const { x, y } = DismissableTooltip.getTooltipPoint(associatedElement, tooltip, position);
            DismissableTooltip.transformXY(tooltip, DismissableTooltipPosition_1.DismissableTooltipPosition.TOP_RIGHT, x, y);
        }
        else {
            const { x, y } = DismissableTooltip.getTooltipPoint(associatedElement, tooltip, position);
            DismissableTooltip.transformXY(tooltip, position, x, y);
        }
    }
    static transformXY(tooltip, position, x, y) {
        tooltip.className = "";
        tooltip.classList.add("dismissable-tooltip", position);
        tooltip.style.transform = `translateX(${x}px) translateY(${y}px)`;
    }
    static getTooltipPoint(associatedElement, tooltip, position) {
        let x = 0;
        let y = 0;
        switch (position) {
            case DismissableTooltipPosition_1.DismissableTooltipPosition.TOP_RIGHT:
                x = -(tooltip.clientWidth / 2 - associatedElement.clientWidth / 2);
                y = -tooltip.clientHeight - 20;
                break;
            case DismissableTooltipPosition_1.DismissableTooltipPosition.BOTTOM_RIGHT:
                x = associatedElement.clientWidth + 20;
                y = -(tooltip.clientHeight / 2) + (associatedElement.clientHeight / 2);
                break;
            case DismissableTooltipPosition_1.DismissableTooltipPosition.BOTTOM_RIGHT:
                x = -(tooltip.clientWidth / 2 - associatedElement.clientWidth / 2);
                y = associatedElement.clientHeight + 20;
                break;
            case DismissableTooltipPosition_1.DismissableTooltipPosition.BOTTOM_LEFT:
                x = -tooltip.clientWidth - 20;
                y = -(tooltip.clientHeight / 2) + (associatedElement.clientHeight / 2);
                break;
        }
        return { x, y };
    }
    static isElementInDom(rect) {
        return rect.left > 0 &&
            rect.bottom > 0 &&
            rect.top > 0 &&
            rect.right > 0;
    }
}
exports.default = DismissableTooltip;
//# sourceMappingURL=DismissableTooltip.js.map